/**
 * Today — what this building needs from the provider now (EP-004P Phase 1).
 *
 * == Why the order is this order ==========================================
 *
 * The visit day comes first because it is the frame for everything below:
 * a provider opens this inside the week before a visit, and work with no day
 * attached is a list, not a plan. Devices waiting to be fitted come next
 * because they are the only thing here a provider can finish on site without
 * anyone else. Blocked paperwork follows: also fixable on site, but it needs
 * a person to chase. The census comes last because it is reference, not a
 * task list — a provider who needed the roster first would have opened it.
 *
 * == What is deliberately absent =========================================
 *
 * No completion checkboxes, no status dropdowns, no "mark as fitted". Every
 * one of those fields is Monday-owned, and a control that looked writable and
 * silently wasn't would be worse than no control. The footer says so in the
 * provider's words rather than leaving them to conclude the page is broken.
 *
 * No time of day, anywhere. The business schedules by day and facility; a
 * "10:30" here would be invented.
 */

import React, { useState } from 'react';
import { StateBlock as TideState } from '../../components/tide';
import {
  DaySheet, FitResident, BlockedUmed, RosterPatient, SchedulingRow, FacilityContact, CheckInRecord,
  patientName, shortDate, TESTING_STATUS_LABEL, testingTone, testingTitle, dispositionLine, monthYear,
} from './providerApi';

/** The time of an instant, in the reader's own clock — a check-in is a real moment, not a board date. */
function clockTime(value: string): string {
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

/**
 * Check in — an event, not a checkbox (Provider ruling 2026-09-08).
 *
 *   Check in → choose the recorded contact → Confirm
 *   ✓ Checked in at 8:43 AM with Jane Smith · SSD
 *
 * The contacts offered are the building's recorded ones, SSD first. A
 * building with none recorded still lets the provider check in; it says so
 * and the absence becomes an Operations data-quality item, not a guess.
 */
function CheckIn({
  contacts, today, onCheckIn,
}: {
  contacts: FacilityContact[]; today: CheckInRecord[];
  onCheckIn: (contactId: string | null) => Promise<boolean>;
}) {
  const [open, setOpen] = useState(false);
  const [contactId, setContactId] = useState<string>(contacts[0]?.id || '');
  const [busy, setBusy] = useState(false);
  const latest = today[0];
  if (latest && !open) {
    return (
      <section className="pv-card pv-checkin">
        <div className="pv-card-head">
          <div>
            <h2>Checked in</h2>
            <p className="why">
              {clockTime(latest.checked_in_at)}
              {latest.contact_name_snapshot
                ? ` with ${latest.contact_name_snapshot}${latest.contact_role_snapshot ? ` · ${latest.contact_role_snapshot}` : ''}`
                : ' — no contact named'}
            </p>
          </div>
          <span className="pv-tag ok">✓</span>
        </div>
      </section>
    );
  }
  return (
    <section className="pv-card pv-checkin">
      <div className="pv-card-head">
        <div>
          <h2>Check in</h2>
          <p className="why">
            {contacts.length
              ? 'Record that you have arrived, and who you checked in with.'
              : 'No contact is recorded for this building yet. You can still check in.'}
          </p>
        </div>
        {!open && <button type="button" className="pv-open" onClick={() => setOpen(true)}>Check in</button>}
      </div>
      {open && (
        <form
          className="pv-checkin-form"
          onSubmit={async (e) => {
            e.preventDefault();
            setBusy(true);
            const ok = await onCheckIn(contactId || null);
            setBusy(false);
            if (ok) setOpen(false);
          }}
        >
          {contacts.length > 0 && (
            <label>
              <span>With</span>
              <select value={contactId} onChange={(e) => setContactId(e.target.value)} aria-label="Checked in with">
                {contacts.map((c) => (
                  <option key={c.id} value={c.id}>{c.name || 'Name not recorded'}{c.title ? ` · ${c.title}` : ''}</option>
                ))}
                <option value="">Nobody named</option>
              </select>
            </label>
          )}
          <button type="submit" className="pv-open pv-primary" disabled={busy}>{busy ? 'Recording…' : 'Confirm'}</button>
          <button type="button" className="pv-clear" onClick={() => setOpen(false)}>Cancel</button>
        </form>
      )}
    </section>
  );
}

/** The testing status, said once, with the conflict named when there is one. */
function TestingBadge({ patient }: { patient: RosterPatient }) {
  if (!patient.testing_status) return null;
  const tone = testingTone(patient.testing_status);
  return (
    <span className={`pv-tag${tone ? ` ${tone}` : ''}`} title={testingTitle(patient)}>
      {TESTING_STATUS_LABEL[patient.testing_status]}
      {patient.testing_status_conflict ? ' · conflicting' : ''}
      {patient.testing_status_drift ? ' · Monday differs' : ''}
    </span>
  );
}

/** Room number, or a placeholder that does not look like a room number. */
function Room({ value }: { value: string | null }) {
  return <span className="pv-room">{value || '—'}</span>;
}

/**
 * The calendar day of a Monday date, read as the UTC date.
 *
 * Monday stores its dates as midnight UTC. Reading one in local time puts
 * every visit on the previous evening, which is the defect the coverage board
 * shipped with on its first day. So the UTC calendar date is the date, and
 * the relative distance is measured from the reader's own calendar day.
 */
function utcDay(value: string | null): { label: string; days: number } | null {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  const y = d.getUTCFullYear();
  const m = d.getUTCMonth();
  const day = d.getUTCDate();
  const label = new Date(Date.UTC(y, m, day)).toLocaleDateString('en-US', {
    timeZone: 'UTC', weekday: 'short', month: 'short', day: 'numeric',
  });
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const that = new Date(y, m, day).getTime();
  return { label, days: Math.round((that - today) / 86400000) };
}

function relative(days: number): string {
  if (days === 0) return 'today';
  if (days === 1) return 'tomorrow';
  if (days === -1) return 'yesterday';
  return days > 0 ? `in ${days} days` : `${Math.abs(days)} days ago`;
}

/**
 * The facility day — where am I going, and when.
 *
 * One line, the calendar date only. A next visit already behind us is shown
 * as such rather than hidden: the board is the schedule, and a stale date on
 * it is a fact for the scheduling team, not something for this screen to
 * quietly tidy. Several scheduling rows is also a fact worth seeing, and the
 * Schedule view renders all of them.
 */
function VisitDay({ scheduling }: { scheduling: SchedulingRow[] }) {
  if (scheduling.length > 1) {
    return (
      <p className="pv-day">
        <span className="pv-day-label">Visit</span>
        <span>{scheduling.length} scheduling records for this facility — see Schedule.</span>
      </p>
    );
  }
  const day = scheduling.length === 1 ? utcDay(scheduling[0].provider_next_visit_at) : null;
  if (!day) {
    return (
      <p className="pv-day">
        <span className="pv-day-label">Visit</span>
        <span className="pv-none">No next visit recorded on the scheduling board.</span>
      </p>
    );
  }
  return (
    <p className="pv-day">
      <span className="pv-day-label">Next visit</span>
      <strong>{day.label}</strong>
      <span className={`pv-day-rel${day.days < 0 ? ' stop' : ''}`}>{relative(day.days)}</span>
    </p>
  );
}

function SectionHead({
  title, why, count, tone,
}: {
  title: string;
  why: string;
  count: number;
  tone?: 'attn' | 'stop';
}) {
  return (
    <div className="pv-card-head">
      <div>
        <h2>{title}</h2>
        <p className="why">{why}</p>
      </div>
      {/* Zero is styled as resolved, not as an alarm reading zero. */}
      <span className={`pv-count${count > 0 && tone ? ` ${tone}` : ''}`}>{count}</span>
    </div>
  );
}

/**
 * One resident, their devices underneath (April Torino, 2026-09-11: "Is
 * there a way to reflect them by one resident? Victoria Charles — ATD-BOTH").
 * The disposition line says what kind of dispense and which ears; the device
 * rows keep type and serial. A serial is only assigned after dispense, so an
 * empty one reads "not yet assigned", never "missing".
 */
function FitResidentRow({ r }: { r: FitResident }) {
  return (
    <li className="pv-row">
      <Room value={r.room_number} />
      <div className="who">
        <strong>{patientName(r)}</strong>
        <span>{dispositionLine(r.disposition)}</span>
        <ul className="pv-devices">
          {r.devices.map((d) => {
            const ear = d.ear ? d.ear.trim() : null;
            return (
              <li key={d.id}>
                <span>{[ear ? `${ear} ear` : null, d.hearing_aid_type].filter(Boolean).join(' · ') || 'Device details not recorded'}</span>
                {d.serial_number ? <span className="num">{d.serial_number}</span> : <span className="pv-none">Serial not yet assigned</span>}
              </li>
            );
          })}
        </ul>
      </div>
    </li>
  );
}

function UmedRow({ umed }: { umed: BlockedUmed }) {
  const blocker = (umed.faxed_status || '').trim();
  return (
    <li className="pv-row">
      <Room value={umed.room_number} />
      <div className="who">
        <strong>{patientName(umed)}</strong>
        <span>
          {umed.submitted_date
            ? `Submitted ${shortDate(umed.submitted_date)}`
            : 'Not yet submitted'}
          {umed.last_follow_up_date
            ? ` · last chased ${shortDate(umed.last_follow_up_date)}`
            : ''}
        </span>
      </div>
      <div className="aside">
        {/* The blocker verbatim from the source. Not remapped into a tidier
            vocabulary — EP-004P forbids inventing workflow states, and the
            provider recognises these words from Monday. */}
        <span className="pv-tag stop">{blocker || 'blocked'}</span>
      </div>
    </li>
  );
}

function RosterRow({ patient }: { patient: RosterPatient }) {
  return (
    <li className="pv-row">
      <Room value={patient.room_number} />
      <div className="who">
        <strong>{patientName(patient)}</strong>
        <span>
          {[patient.status, patient.gender, patient.phase].filter(Boolean).join(' · ') || 'No status recorded'}
          {' '}
          <TestingBadge patient={patient} />
          {patient.new_referral && <span className="pv-tag ok">new referral</span>}
          {patient.annual_due && <span className="pv-tag attn">annual due</span>}
          {patient.manifest_exclusion === 'SCREENED_DNQ' && (
            <span className="pv-none">
              {' '}· screened{patient.dnq_date ? ` ${shortDate(patient.dnq_date)}` : ''}{monthYear(patient.annual_appt_date) ? `, annual ${monthYear(patient.annual_appt_date)}` : ''}
            </span>
          )}
        </span>
      </div>
      <div className="aside">
        {/*
          Two different empties, said differently — the same rule the manifest
          uses. A resident the caller is care-assigned to but who has no
          CounselEAR id gets a dash: there is no chart locator. A resident the
          caller is not assigned to gets "withheld": there may well be one,
          and it was not released. Rendering the second as the first told a
          provider they lacked access they had; rendering the first as the
          second invented a policy where there was only an empty field.

          The id is labelled because on its own it is digits in a chip, which
          reads as a status. It is a record locator for CounselEAR, and it
          says so in the same tone as the manifest's column.
        */}
        {patient.care_assigned
          ? (patient.counselear_id
            ? (
              <span className="pv-cid">
                <span className="pv-cid-label">CounselEAR</span>
                <span>{patient.counselear_id}</span>
              </span>
            )
            : <span className="pv-none">—</span>)
          : <span className="pv-mf-withheld">withheld</span>}
      </div>
    </li>
  );
}

export default function Today({
  sheet, onPage, onCheckIn,
}: {
  sheet: DaySheet;
  onPage: (offset: number) => void;
  onCheckIn: (contactId: string | null) => Promise<boolean>;
}) {
  const { blockedUmed, roster, scheduling } = sheet;
  const fitResidents = sheet.fitResidents || [];
  const dispensedElsewhere = sheet.counts.dispensedElsewhere || 0;
  const pageEnd = roster.offset + roster.returned;
  const hasMore = pageEnd < roster.total;

  return (
    <>
      <VisitDay scheduling={scheduling} />

      <CheckIn contacts={sheet.contacts.all} today={sheet.checkIn.today} onCheckIn={onCheckIn} />

      <section className="pv-card">
        <SectionHead
          title="Waiting to be fitted"
          why="Residents with devices ordered here and not yet dispensed — by CounselEAR's record or the provider's, not only the board's."
          count={fitResidents.length}
          tone="attn"
        />
        {fitResidents.length === 0
          ? <TideState kind="empty" reason="none" kindLabel="Nothing waiting" message="Nothing waiting. Every device ordered for this building is recorded as dispensed." />
          : <ul className="pv-rows">{fitResidents.map((r) => <FitResidentRow key={r.patient_id} r={r} />)}</ul>}
        {/*
          The disagreement is said, never averaged (packet §15): the board
          still shows these undelivered; CounselEAR or the provider's dispense
          record says otherwise, and that record wins. Counted for the office
          to reconcile the board.
        */}
        {dispensedElsewhere > 0 && (
          <p className="pv-mf-withheld">
            {dispensedElsewhere} {dispensedElsewhere === 1 ? 'device the board still shows as undelivered is' : 'devices the board still shows as undelivered are'} recorded as dispensed by CounselEAR or the provider's dispense sheet, so {dispensedElsewhere === 1 ? 'it is' : 'they are'} not listed here.
          </p>
        )}
      </section>

      <section className="pv-card">
        <SectionHead
          title="Blocked, and fixable here"
          why="UMED requests held up by something obtainable on site."
          count={blockedUmed.length}
          tone="stop"
        />
        {blockedUmed.length === 0
          ? <TideState kind="empty" reason="none" kindLabel="Nothing waiting" message="Nothing blocked on anything you can collect at this facility." />
          : <ul className="pv-rows">{blockedUmed.map((u) => <UmedRow key={u.id} umed={u} />)}</ul>}
      </section>

      <section className="pv-card">
        <SectionHead
          title="Residents"
          why="Everyone with a confirmed placement at this facility."
          count={roster.total}
        />
        {roster.rows.length === 0
          ? <TideState kind="empty" reason="none" message="No residents are currently confirmed at this facility." />
          : <ul className="pv-rows">{roster.rows.map((p) => <RosterRow key={p.id} patient={p} />)}</ul>}

        {roster.total > roster.returned && (
          <div className="pv-pager">
            <span className="num">
              {roster.offset + 1}–{pageEnd} of {roster.total}
            </span>
            <div className="btns">
              <button
                type="button"
                disabled={roster.offset === 0}
                onClick={() => onPage(Math.max(0, roster.offset - roster.limit))}
              >
                Previous
              </button>
              <button
                type="button"
                disabled={!hasMore}
                onClick={() => onPage(roster.offset + roster.limit)}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </section>

      {/*
        In the provider's words. The payload's provenance strings name the
        columns and the policy that own these fields, which is the right
        record for an engineer reading the API and the wrong sentence for a
        provider standing in a corridor. They stay in the payload; they do
        not render here.
      */}
      <div className="pv-foot">
        <div className="pv-note">
          <span>
            <strong>Checking in is the one thing recorded here.</strong>{' '}
            Fitting, delivery and visit status are recorded in Monday. This screen reads
            them and cannot change them.
          </span>
          <span>Residents shown are those with a confirmed placement at this facility.</span>
        </div>
      </div>
    </>
  );
}
